import { useEffect, useState } from "react";
import { Send, TrendingUp, Award, Clock, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import DashboardShell, { ColorStat } from "@/components/DashboardShell";

const STATUSES = ["pending", "in_progress", "closed_won", "closed_lost"];

export default function Referrals() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("give");
  const [analytics, setAnalytics] = useState(null);
  const [refs, setRefs] = useState([]);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    to_member_id: "", contact_name: "", contact_phone: "", contact_email: "", notes: "", value: 0,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    refresh();
    api.get("/members", { params: { limit: 100 } }).then(r => setMembers(r.data));
  }, [user]); // eslint-disable-line

  const refresh = async () => {
    try {
      const [a, r] = await Promise.all([
        api.get("/referrals/analytics").then(r => r.data),
        api.get("/referrals/my").then(r => r.data),
      ]);
      setAnalytics(a); setRefs(r);
    } catch {}
  };

  if (loading) return <div className="container-luxe py-32 text-center text-plumSoft">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.to_member_id) { toast.error("Select a member"); return; }
    setBusy(true);
    try {
      await api.post("/referrals", form);
      toast.success("Referral sent!");
      setForm({ to_member_id: "", contact_name: "", contact_phone: "", contact_email: "", notes: "", value: 0 });
      refresh();
      setTab("track");
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const setStatus = async (ref_id, status) => {
    try {
      await api.patch(`/referrals/${ref_id}/status`, { status });
      toast.success("Status updated");
      refresh();
    } catch { toast.error("Failed"); }
  };

  const filtered = refs.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.contact_name || "").toLowerCase().includes(s) || (r.notes || "").toLowerCase().includes(s);
  });

  return (
    <DashboardShell role={user?.role === "admin" ? "admin" : "member"} title="Referrals Network" subtitle="Give, receive, track">
      <div data-testid="referrals-page">
        {/* Analytics */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <ColorStat icon={Send} label="Given" value={analytics.given} tone="purple" />
            <ColorStat icon={Award} label="Received" value={analytics.received} tone="rose" />
            <ColorStat icon={TrendingUp} label="Closed Won" value={analytics.closed_won} tone="teal" />
            <ColorStat icon={Clock} label="Conversion" value={`${analytics.conversion_rate}%`} tone="gold" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: "give", label: "Give Referral" },
            { id: "track", label: "My Referrals" },
            { id: "stories", label: "Success Stories" },
          ].map(t => (
            <button key={t.id} data-testid={`ref-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest border ${tab === t.id ? "bg-aubergine text-ivory border-aubergine" : "border-aubergine/20 text-plum"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "give" && (
          <form onSubmit={submit} className="card-luxe p-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <h2 className="font-display text-2xl text-aubergine md:col-span-2">Send a warm introduction</h2>
            <div className="md:col-span-2">
              <label className="label-luxe">To Member *</label>
              <select required data-testid="ref-to" value={form.to_member_id} onChange={(e) => setForm({ ...form, to_member_id: e.target.value })} className="input-luxe">
                <option value="">Select a WOWSHE member…</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name} · {m.business}</option>)}
              </select>
            </div>
            <FInp label="Contact Name *" required v={form.contact_name} k="contact_name" set={setForm} form={form} t="ref-cname" />
            <FInp label="Contact Phone *" required v={form.contact_phone} k="contact_phone" set={setForm} form={form} t="ref-cphone" />
            <FInp label="Contact Email" type="email" v={form.contact_email} k="contact_email" set={setForm} form={form} t="ref-cemail" />
            <FInp label="Est. Value (₹)" type="number" v={form.value} k="value" set={setForm} form={form} t="ref-value" />
            <div className="md:col-span-2">
              <label className="label-luxe">Context</label>
              <textarea data-testid="ref-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-luxe" placeholder="What does this person need? Why is this member a good match?" />
            </div>
            <button data-testid="ref-submit" disabled={busy} className="btn-primary md:col-span-2">
              {busy ? "Sending…" : "Send Referral"}
            </button>
          </form>
        )}

        {tab === "track" && (
          <div>
            <div className="relative mb-5 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-plum" />
              <input data-testid="ref-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search referrals…" className="input-luxe pl-11" />
            </div>
            {filtered.length === 0 ? (
              <div className="card-luxe p-10 text-center text-plumSoft font-light">No referrals yet.</div>
            ) : (
              <div className="space-y-3">
                {filtered.map(r => {
                  const direction = r.from_user_id === user.id ? "Given" : "Received";
                  return (
                    <div key={r.id} data-testid={`ref-row-${r.id}`} className="card-luxe p-5 flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] tracking-widest uppercase text-plum">{direction}</span>
                          {r.value > 0 && <span className="text-[10px] tracking-widest uppercase text-champagne">₹ {Number(r.value).toLocaleString("en-IN")}</span>}
                        </div>
                        <p className="font-display text-lg text-aubergine">{r.contact_name}</p>
                        <p className="text-xs text-plumSoft mt-1">{r.contact_phone} {r.contact_email && ` · ${r.contact_email}`}</p>
                        {r.notes && <p className="text-sm text-plumSoft mt-2 font-light max-w-xl">{r.notes}</p>}
                      </div>
                      <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="input-luxe !w-auto !py-2 text-xs">
                        {STATUSES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "stories" && (
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { quote: "A referral from a fellow WOWSHE member became my first ₹10L client.", who: "Founding Member · Hyderabad" },
              { quote: "Hands down the best ROI from any community I've ever joined.", who: "Founding Member · Hyderabad" },
            ].map((s, i) => (
              <div key={i} className="card-luxe p-7">
                <p className="text-rose">"</p>
                <p className="font-display text-xl text-aubergine leading-snug">{s.quote}</p>
                <p className="text-xs uppercase tracking-widest text-plum mt-5">{s.who}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div className={`card-luxe p-5 ${accent ? "bg-gradient-to-br from-rose/10 to-champagne/10" : ""}`}>
      <div className="w-9 h-9 rounded-full bg-lavender text-plum flex items-center justify-center">{icon}</div>
      <p className="overline mt-3">{label}</p>
      <p className="font-display text-2xl text-aubergine mt-1">{value}</p>
    </div>
  );
}

function FInp({ label, v, k, set, form, t, required, type = "text" }) {
  return (
    <div>
      <label className="label-luxe">{label}</label>
      <input data-testid={t} type={type} required={required} value={v} onChange={(e) => set({ ...form, [k]: e.target.value })} className="input-luxe" />
    </div>
  );
}
